// Location API Service - No-Auth Master Data
import { apiRequest } from '../services/apiClient';

const BASE_URL = '/gateway/registration/api/no-auth/locations';

/**
 * Location Service
 * Provides access to master location data (Country, County, Sub County, Ward)
 * No authentication required - public master data
 */
export const locationService = {
  
  // ========== COUNTRY ENDPOINTS ==========
  
  /**
   * Get all active countries
   * GET /gateway/registration/api/no-auth/locations/countries
   */
  getActiveCountries: async () => {
    try {
      const response = await apiRequest(`${BASE_URL}/countries`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching active countries:', error);
      throw error;
    }
  },
  
  /**
   * Get all countries (including inactive)
   * GET /gateway/registration/api/no-auth/locations/countries/all
   */
  getAllCountries: async () => {
    try {
      const response = await apiRequest(`${BASE_URL}/countries/all`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching all countries:', error);
      throw error;
    }
  },
  
  /**
   * Get country by ID
   * GET /gateway/registration/api/no-auth/locations/countries/{id}
   */
  getCountryById: async (id) => {
    try {
      const response = await apiRequest(`${BASE_URL}/countries/${id}`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching country by ID:', error);
      throw error;
    }
  },
  
  // ========== COUNTY ENDPOINTS ==========
  
  /**
   * Get all active counties
   * GET /gateway/registration/api/no-auth/locations/counties?countryId={countryId}
   * 
   * @param {number} countryId Optional country ID to filter counties
   */
  getActiveCounties: async (countryId = null) => {
    try {
      const url = countryId 
        ? `${BASE_URL}/counties?countryId=${countryId}`
        : `${BASE_URL}/counties`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching active counties:', error);
      throw error;
    }
  },
  
  /**
   * Get all counties (including inactive)
   * GET /gateway/registration/api/no-auth/locations/counties/all?countryId={countryId}
   * 
   * @param {number} countryId Optional country ID to filter counties
   */
  getAllCounties: async (countryId = null) => {
    try {
      const url = countryId 
        ? `${BASE_URL}/counties/all?countryId=${countryId}`
        : `${BASE_URL}/counties/all`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching all counties:', error);
      throw error;
    }
  },
  
  /**
   * Get county by ID
   * GET /gateway/registration/api/no-auth/locations/counties/{id}
   */
  getCountyById: async (id) => {
    try {
      const response = await apiRequest(`${BASE_URL}/counties/${id}`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching county by ID:', error);
      throw error;
    }
  },
  
  // ========== SUB COUNTY ENDPOINTS ==========
  
  /**
   * Get all active sub counties
   * GET /gateway/registration/api/no-auth/locations/sub-counties?countyId={countyId}
   * 
   * @param {number} countyId Optional county ID to filter sub counties
   */
  getActiveSubCounties: async (countyId = null) => {
    try {
      const url = countyId 
        ? `${BASE_URL}/sub-counties?countyId=${countyId}`
        : `${BASE_URL}/sub-counties`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching active sub counties:', error);
      throw error;
    }
  },
  
  /**
   * Get all sub counties (including inactive)
   * GET /gateway/registration/api/no-auth/locations/sub-counties/all?countyId={countyId}
   * 
   * @param {number} countyId Optional county ID to filter sub counties
   */
  getAllSubCounties: async (countyId = null) => {
    try {
      const url = countyId 
        ? `${BASE_URL}/sub-counties/all?countyId=${countyId}`
        : `${BASE_URL}/sub-counties/all`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching all sub counties:', error);
      throw error;
    }
  },
  
  /**
   * Get sub county by ID
   * GET /gateway/registration/api/no-auth/locations/sub-counties/{id}
   */
  getSubCountyById: async (id) => {
    try {
      const response = await apiRequest(`${BASE_URL}/sub-counties/${id}`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching sub county by ID:', error);
      throw error;
    }
  },
  
  // ========== WARD ENDPOINTS ==========
  
  /**
   * Get all active wards
   * GET /gateway/registration/api/no-auth/locations/wards?subCountyId={subCountyId}
   * 
   * @param {number} subCountyId Optional sub county ID to filter wards
   */
  getActiveWards: async (subCountyId = null) => {
    try {
      const url = subCountyId 
        ? `${BASE_URL}/wards?subCountyId=${subCountyId}`
        : `${BASE_URL}/wards`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching active wards:', error);
      throw error;
    }
  },
  
  /**
   * Get all wards (including inactive)
   * GET /gateway/registration/api/no-auth/locations/wards/all?subCountyId={subCountyId}
   * 
   * @param {number} subCountyId Optional sub county ID to filter wards
   */
  getAllWards: async (subCountyId = null) => {
    try {
      const url = subCountyId 
        ? `${BASE_URL}/wards/all?subCountyId=${subCountyId}`
        : `${BASE_URL}/wards/all`;
      const response = await apiRequest(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching all wards:', error);
      throw error;
    }
  },
  
  /**
   * Get ward by ID
   * GET /gateway/registration/api/no-auth/locations/wards/{id}
   */
  getWardById: async (id) => {
    try {
      const response = await apiRequest(`${BASE_URL}/wards/${id}`, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Error fetching ward by ID:', error);
      throw error;
    }
  }
};

export default locationService;

